import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";

// Import existing components
import Profile from "../Profile";

// Import new selection pages
import OrganizationSelectionPage from "../organizations/OrganizationSelectionPage";
import BusinessUnitSelectionPage from "../BusinessUnit/BusinessUnitSelectionPage";

// Import shared dashboard components
import OrganizationDashboard from "../organizations/OrganizationDashboard";
import BusinessUnitDashboard from "../BusinessUnit/BusinessUnitDashboard";

// Import unified components
import UnifiedRisksPage from "../Unified/UnifiedRisksPage";
import UnifiedControlsPage from "../Unified/UnifiedControlsPage";
import UnifiedUsersPage from "../Unified/UnifiedUsersPage";
import BusinessUnitSettingsPage from "../BusinessUnit/BusinessUnitSettingsPage";
import BusinessUnitInvitesPage from "../Invites/BusinessUnitInvitesPage";
import BusinessUnitAuditPage from "../Audit/BusinessUnitAuditPage";
import OrganizationInvitesPage from "../Invites/OrganizationInvitesPage";
import OrganizationMetadataPage from "../Metadata/OrganizationMetadataPage";
import OrganizationMigrationPage from "../Common/OrganizationMigrationPage";
import OrganizationAuditPage from "../Audit/OrganizationAuditPage";
import AuditLogViewer from "../Audit/AuditLogViewer";

// Business User specific components
import MyAssignedControls from "../Controls/MyAssignedControls";

// Import auto-routing handler
import AutoRouteHandler from "./AutoRouteHandler";

const AccessDenied = () => (
  <div className="container mx-auto px-6 py-8">
    <div className="text-center">
      <h1 className="text-3xl font-bold mb-4">Access Denied</h1>
      <p className="text-muted-foreground">
        You don't have permission to access this page.
      </p>
    </div>
  </div>
);

const RoleBasedRoutes = () => {
  const {
    isOrgAdmin,
    isBusinessUnitHead,
    isBusinessUser,
    primaryRole,
    hasAnyRole,
  } = useUser();

  // If user has no role, redirect to profile
  if (!hasAnyRole) {
    return (
      <Routes>
        <Route path="/profile" element={<Profile />} />
        <Route path="*" element={<Navigate to="/profile" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Auto-route handler for initial routing */}
      <Route path="/" element={<AutoRouteHandler />} />

      {/* Common routes */}
      <Route path="/profile" element={<Profile />} />

      {/* Organization Admin routes */}
      {isOrgAdmin && (
        <>
          {/* Organization selection page */}
          <Route
            path="/organizations"
            element={<OrganizationSelectionPage />}
          />
          {/* Individual organization dashboard */}
          <Route
            path="/organization/:organizationId/dashboard"
            element={<OrganizationDashboard />}
          />
          {/* Organization-specific routes */}
          <Route
            path="/organization/:organizationId/business-units"
            element={
              <BusinessUnitSelectionPage mode="organization-management" />
            }
          />
          <Route
            path="/organization/:organizationId/risks"
            element={<UnifiedRisksPage />}
          />
          <Route
            path="/organization/:organizationId/controls"
            element={<UnifiedControlsPage />}
          />
          <Route
            path="/organization/:organizationId/users"
            element={<UnifiedUsersPage />}
          />
          <Route
            path="/organization/:organizationId/invites"
            element={<OrganizationInvitesPage />}
          />
          <Route
            path="/organization/:organizationId/settings"
            element={<OrganizationMetadataPage />}
          />
          <Route
            path="/organization/:organizationId/migration"
            element={<OrganizationMigrationPage />}
          />
          <Route
            path="/organization/:organizationId/reports/audit"
            element={<OrganizationAuditPage />}
          />
        </>
      )}

      {/* Business Unit Head routes */}
      {isBusinessUnitHead && (
        <>
          {/* Business unit selection page */}
          <Route
            path="/business-units"
            element={<BusinessUnitSelectionPage />}
          />
          {/* Individual business unit dashboard */}
          <Route
            path="/business-unit/:businessUnitId/dashboard"
            element={<BusinessUnitDashboard />}
          />
          {/* Business unit specific routes */}
          <Route
            path="/business-unit/:businessUnitId/users"
            element={<UnifiedUsersPage />}
          />
          <Route
            path="/business-unit/:businessUnitId/settings"
            element={<BusinessUnitSettingsPage />}
          />
          <Route
            path="/business-unit/:businessUnitId/risks"
            element={<UnifiedRisksPage />}
          />
          <Route
            path="/business-unit/:businessUnitId/controls"
            element={<UnifiedControlsPage />}
          />
          <Route
            path="/business-unit/:businessUnitId/reports/audit"
            element={<BusinessUnitAuditPage />}
          />
          <Route
            path="/business-unit/:businessUnitId/invites"
            element={<BusinessUnitInvitesPage />}
          />
        </>
      )}

      {/* Business User routes */}
      {isBusinessUser && (
        <>
          {/* Business unit selection page */}
          <Route
            path="/business-units"
            element={<BusinessUnitSelectionPage />}
          />
          {/* Individual business unit dashboard (with restricted access) */}
          <Route
            path="/business-unit/:businessUnitId/dashboard"
            element={<BusinessUnitDashboard />}
          />
          {/* Business unit specific routes for business users */}
          <Route
            path="/business-unit/:businessUnitId/controls"
            element={<UnifiedControlsPage />}
          />
          <Route
            path="/business-unit/:businessUnitId/reports/audit"
            element={<BusinessUnitAuditPage />}
          />
          {/* My assigned controls page */}
          <Route path="/my-controls" element={<MyAssignedControls />} />
        </>
      )}

      {/* Shared routes that organization admins can access */}
      {isOrgAdmin && (
        <>
          {/* Org admins can also access business unit dashboards */}
          <Route
            path="/business-unit/:businessUnitId/dashboard"
            element={<BusinessUnitDashboard />}
          />
          {/* Org admins can access business unit specific routes */}
          <Route
            path="/business-unit/:businessUnitId/users"
            element={<UnifiedUsersPage />}
          />
          <Route
            path="/business-unit/:businessUnitId/settings"
            element={<BusinessUnitSettingsPage />}
          />
          <Route
            path="/business-unit/:businessUnitId/risks"
            element={<UnifiedRisksPage />}
          />
          <Route
            path="/business-unit/:businessUnitId/controls"
            element={<UnifiedControlsPage />}
          />
          <Route
            path="/business-unit/:businessUnitId/reports/audit"
            element={<BusinessUnitAuditPage />}
          />
          <Route
            path="/business-unit/:businessUnitId/invites"
            element={<BusinessUnitInvitesPage />}
          />
        </>
      )}

      {/* Global Reports Routes - available to all roles */}
      <Route path="/reports/audit" element={<AuditLogViewer />} />

      {/* Fallback for unauthorized access */}
      <Route path="*" element={<AccessDenied />} />
    </Routes>
  );
};

export default RoleBasedRoutes;
